import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useResponsive } from "../../../hooks/useResponsive";
import { setGroupUser } from "../../../redux/TaskGroupingSlice";

export function useUnassignedUsersAccordionState({
  unAssignedTeam,
  user,
  setUnassigned,
  searchQuery,
  openUnassigned,
  selectedUserIds = [],
  syncSelectedUsers,
}) {
  const dispatch = useDispatch();
  const isMobile = useResponsive("down", "md");
  const [manualOverride, setManualOverride] = useState(false);
  const [wasSearching, setWasSearching] = useState(false);

  const shouldBeExpanded = useMemo(() => {
    return searchQuery && !manualOverride
      ? unAssignedTeam.length > 0
      : openUnassigned;
  }, [manualOverride, openUnassigned, searchQuery, unAssignedTeam.length]);

  const selectedUserIdSet = useMemo(() => {
    const ids = isMobile
      ? user.map((selectedUser) => String(selectedUser.user_id))
      : selectedUserIds;

    return new Set(ids.map(String));
  }, [isMobile, selectedUserIds, user]);

  const areAllUnassignedSelected = useMemo(() => {
    if (!unAssignedTeam.length) return false;

    return unAssignedTeam.every((unassignedUser) =>
      selectedUserIdSet.has(String(unassignedUser.user_id))
    );
  }, [selectedUserIdSet, unAssignedTeam]);

  useEffect(() => {
    if (searchQuery) {
      setWasSearching(true);
    } else if (wasSearching) {
      setManualOverride(false);
      setUnassigned(false);
      setWasSearching(false);
    }
  }, [searchQuery, setUnassigned, wasSearching]);

  const handleAccordionChange = useCallback(
    (_event, isExpanded) => {
      if (searchQuery) {
        setManualOverride(true);
      }

      setUnassigned(isExpanded);
    },
    [searchQuery, setUnassigned]
  );

  const handleCheckboxClick = useCallback(
    (event) => {
      event.stopPropagation();

      if (isMobile) {
        return;
      }

      const unassignedUserIds = unAssignedTeam.map((teamUser) =>
        String(teamUser.user_id)
      );
      const nextSelectedUserIds = areAllUnassignedSelected
        ? selectedUserIds.filter((userId) => !unassignedUserIds.includes(userId))
        : [
            ...selectedUserIds,
            ...unassignedUserIds.filter(
              (userId) => !selectedUserIdSet.has(String(userId))
            ),
          ];

      syncSelectedUsers(nextSelectedUserIds, unAssignedTeam);
    },
    [
      areAllUnassignedSelected,
      isMobile,
      selectedUserIdSet,
      selectedUserIds,
      syncSelectedUsers,
      unAssignedTeam,
    ]
  );

  const handleUserClick = useCallback(
    (clickedUser) => {
      if (isMobile) {
        dispatch(setGroupUser(clickedUser));
        return;
      }

      const clickedUserId = String(clickedUser.user_id);
      const nextSelectedUserIds = selectedUserIdSet.has(clickedUserId)
        ? selectedUserIds.filter((userId) => userId !== clickedUserId)
        : [...selectedUserIds, clickedUserId];

      syncSelectedUsers(nextSelectedUserIds, [clickedUser]);
    },
    [dispatch, isMobile, selectedUserIdSet, selectedUserIds, syncSelectedUsers]
  );

  const tooltipTitle = useMemo(() => {
    return searchQuery && shouldBeExpanded ? "Click to collapse" : "";
  }, [searchQuery, shouldBeExpanded]);

  return {
    areAllUnassignedSelected,
    handleAccordionChange,
    handleCheckboxClick,
    handleUserClick,
    selectedUserIdSet,
    shouldBeExpanded,
    tooltipTitle,
  };
}
